import { ContentState, DraftBlockType, DraftInlineStyleType, RawDraftContentBlock, convertFromRaw, genKey } from "draft-js";

type TDraftInlineStyleRange = {
  offset: number;
  length: number;
  style: string;
};

const createRawBlock = (type: DraftBlockType, text: string, inlineStyleRanges: TDraftInlineStyleRange[] = []): RawDraftContentBlock => ({
  key: genKey(),
  type,
  text,
  depth: 0,
  inlineStyleRanges: inlineStyleRanges as Array<{ offset: number; length: number; style: DraftInlineStyleType }>,
  entityRanges: [],
  data: {},
});

/** Invisible placeholder so Draft.js keeps blank lines when parsing HTML (empty `<p>` tags are dropped). */
export const EDITOR_LINE_BREAK_PLACEHOLDER = "\u200b";

const NBSP_HTML = "&nbsp;";

const preprocessHtmlForEditor = (html: string): string => {
  if (!html) return html;

  return html
    .replace(/<p><br\s*\/?><\/p>/gi, `<p>${EDITOR_LINE_BREAK_PLACEHOLDER}</p>`)
    .replace(/<p><\/p>/gi, `<p>${EDITOR_LINE_BREAK_PLACEHOLDER}</p>`);
};

/** Preserve spaces in HTML: browsers collapse consecutive spaces unless encoded as &nbsp;. */
const encodeSpacesInPlainText = (text: string): string =>
  text
    .replace(/^ +/g, (spaces) => NBSP_HTML.repeat(spaces.length))
    .replace(/ +$/g, (spaces) => NBSP_HTML.repeat(spaces.length))
    .replace(/ {2,}/g, (spaces) => NBSP_HTML.repeat(spaces.length));

const encodeSpacesInParagraphInner = (inner: string): string => {
  if (!inner || inner === EDITOR_LINE_BREAK_PLACEHOLDER) return inner;

  if (!/<[a-z][\s\S]*>/i.test(inner)) {
    return encodeSpacesInPlainText(inner);
  }

  const leadingMatch = inner.match(/^([^<]+)/);
  const trailingMatch = inner.match(/>([^<]*)$/);

  let result = inner;

  if (leadingMatch?.[1]) {
    result = encodeSpacesInPlainText(leadingMatch[1]) + result.slice(leadingMatch[1].length);
  }

  if (trailingMatch?.[1] !== undefined) {
    const trailing = trailingMatch[1];
    result = result.slice(0, result.length - trailing.length) + encodeSpacesInPlainText(trailing);
  }

  return result;
};

export const normalizeHtmlFromEditor = (html: string): string => {
  if (!html) return html;

  return html.replace(/<p>\s*<\/p>/gi, `<p>${EDITOR_LINE_BREAK_PLACEHOLDER}</p>`).replace(/<p>([\s\S]*?)<\/p>/gi, (match, inner: string) => {
    if (!inner || inner === EDITOR_LINE_BREAK_PLACEHOLDER) return match;

    return `<p>${encodeSpacesInParagraphInner(inner)}</p>`;
  });
};

const isBlankParagraphElement = (element: HTMLElement): boolean => {
  const inner = element.innerHTML.trim().toLowerCase();

  if (inner === "" || inner === "<br>" || inner === "<br/>" || inner === "<br />") return true;

  return element.textContent === EDITOR_LINE_BREAK_PLACEHOLDER;
};

type TInlineContent = {
  text: string;
  inlineStyleRanges: TDraftInlineStyleRange[];
};

const hasBoldInlineStyle = (element: HTMLElement): boolean => {
  const fontWeight = element.style.fontWeight?.toLowerCase() ?? "";
  if (fontWeight === "bold") return true;

  const numericWeight = Number(fontWeight);
  if (!Number.isNaN(numericWeight) && numericWeight >= 600) return true;

  const styleAttr = element.getAttribute("style")?.toLowerCase() ?? "";
  return /font-weight\s*:\s*(bold|[6-9]00)/.test(styleAttr);
};

const hasStrikethroughInlineStyle = (element: HTMLElement): boolean => {
  const textDecoration = element.style.textDecoration?.toLowerCase() ?? "";
  const textDecorationLine = element.style.textDecorationLine?.toLowerCase() ?? "";
  if (textDecoration.includes("line-through") || textDecorationLine.includes("line-through")) return true;

  const styleAttr = element.getAttribute("style")?.toLowerCase() ?? "";
  return /text-decoration(?:-line)?\s*:\s*[^;]*line-through/.test(styleAttr);
};

const parseInlineContent = (root: HTMLElement): TInlineContent => {
  const styleStack: string[] = [];
  let text = "";
  const rangesByStyle = new Map<string, Array<{ offset: number; length: number }>>();

  const addRange = (style: string, offset: number, length: number) => {
    if (length <= 0) return;

    const ranges = rangesByStyle.get(style) ?? [];
    ranges.push({ offset, length });
    rangesByStyle.set(style, ranges);
  };

  const traverse = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const nodeText = node.textContent ?? "";
      const start = text.length;
      text += nodeText;
      const length = nodeText.length;

      styleStack.forEach((style) => addRange(style, start, length));
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const element = node as HTMLElement;
    const tag = element.tagName.toUpperCase();
    const stylesToAdd: string[] = [];

    if (tag === "STRONG" || tag === "B" || hasBoldInlineStyle(element)) stylesToAdd.push("BOLD");
    if (tag === "STRIKE" || tag === "S" || tag === "DEL" || hasStrikethroughInlineStyle(element)) stylesToAdd.push("STRIKETHROUGH");

    stylesToAdd.forEach((style) => styleStack.push(style));
    element.childNodes.forEach(traverse);
    stylesToAdd.forEach(() => styleStack.pop());
  };

  if (isBlankParagraphElement(root)) {
    return { text: EDITOR_LINE_BREAK_PLACEHOLDER, inlineStyleRanges: [] };
  }

  root.childNodes.forEach(traverse);

  if (!text && !(root.textContent ?? "").length) {
    return { text: EDITOR_LINE_BREAK_PLACEHOLDER, inlineStyleRanges: [] };
  }

  const inlineStyleRanges: TDraftInlineStyleRange[] = [];

  rangesByStyle.forEach((ranges, style) => {
    ranges.forEach(({ offset, length }) => inlineStyleRanges.push({ offset, length, style }));
  });

  return { text, inlineStyleRanges };
};

const createBlockFromElement = (element: HTMLElement, type: DraftBlockType): RawDraftContentBlock => {
  const { text, inlineStyleRanges } = parseInlineContent(element);
  return createRawBlock(type, text, inlineStyleRanges);
};

const parseHtmlToRawBlocks = (html: string): RawDraftContentBlock[] => {
  const container = document.createElement("div");
  container.innerHTML = preprocessHtmlForEditor(html);

  const blocks: RawDraftContentBlock[] = [];

  const appendBlockFromElement = (element: HTMLElement, type: DraftBlockType) => {
    blocks.push(createBlockFromElement(element, type));
  };

  const walkElement = (element: Element) => {
    const tag = element.tagName.toLowerCase();

    if (tag === "ul") {
      element.querySelectorAll(":scope > li").forEach((li) => appendBlockFromElement(li as HTMLElement, "unordered-list-item"));
      return;
    }

    if (tag === "ol") {
      element.querySelectorAll(":scope > li").forEach((li) => appendBlockFromElement(li as HTMLElement, "ordered-list-item"));
      return;
    }

    if (tag === "p") {
      appendBlockFromElement(element as HTMLElement, "unstyled");
      return;
    }

    if (tag === "blockquote") {
      appendBlockFromElement(element as HTMLElement, "blockquote");
    }
  };

  Array.from(container.childNodes).forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      walkElement(node as Element);
      return;
    }

    if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
      blocks.push(createRawBlock("unstyled", node.textContent));
    }
  });

  if (blocks.length === 0) {
    blocks.push(createRawBlock("unstyled", ""));
  }

  return blocks;
};

export const createContentStateFromHtml = (html: string): ContentState => {
  if (!html) return ContentState.createFromText("");

  return convertFromRaw({ blocks: parseHtmlToRawBlocks(html), entityMap: {} });
};
